/**
 * @file A JavaScript module to operate with rectangles.
 * @author Gonzalo Alvarez de Aldecoa <gon@galdecoa.com>
 * @license GPL-3.0-or-later
 * @version 2307.29.001
 */

/** @module box */

// #region Type definions

/**
 * A 2D coordinate.
 * @typedef  {Object} module:box~Coordinates
 * @property {Number} x
 * @property {Number} y
 */

/**
 * Dimensions of a rectangle.
 * @typedef  {Object} module:box~Size
 * @property {Number} width  Difference between maximum and minimum x. Should not be less than `0`.
 * @property {Number} height Difference between maximum and minimum y. Should not be less than `0`.
 */

/**
 * Minimum and maximum values of 2D coordinates.
 * @typedef  {Object} module:box~Extrema
 * @property {module:box~Coordinates} min The minimum values of the coordinates.
 * @property {module:box~Coordinates} max The maximum values of the coordinates.
 */

/**
 * Values describing a box' position and size.
 * @typedef  {Object} module:box~Bounding
 * @property {Number} x      Minimum value of the x coordinates.
 * @property {Number} y      Minimum value of the y coordinates.
 * @property {Number} width  Difference between maximum and minimum x. Should not be less than `0`.
 * @property {Number} height Difference between maximum and minimum y. Should not be less than `0`.
 */

/**
 * All the basic properties of a box.
 * @typedef  {Object} module:box~Properties
 * @property {Number} minX   Minimum value of the x coordinates.
 * @property {Number} minY   Minimum value of the y coordinates.
 * @property {Number} maxX   Maximum value of the x coordinates.
 * @property {Number} maxY   Maximum value of the y coordinates.
 * @property {Number} width  Difference between maximum and minimum x. Should not be less than `0`.
 * @property {Number} height Difference between maximum and minimum y. Should not be less than `0`.
 */

// #endregion Type definions

/** 
 * A representation of a rectangle. 
 * The origin of coordinates is at the top left, with x increasing to the right
 * and y increasing downwards.
 * @class
 */
class Box {

	/** 
	 * The minimum (left-most) value of the x coordinates.
	 * @type {Number} 
	 */
	#minX = 0;
	/** 
	 * The minimum (top) value of the y coordinates.
	 * @type {Number} 
	 */
	#minY = 0;
	/** 
	 * The maximum (right-most) value of the x coordinates.
	 * @type {Number} 
	 */
	#maxX = 0;
	/** 
	 * The maximum (bottom) value of the x coordinates.
	 * @type {Number} 
	 */
	#maxY = 0;
	/** 
	 * The difference between [`maxX`]{@link module:box~Box#maxX} and [`minX`]{@link module:box~Box#minX}.
	 * @type {Number} 
	 */
	#width = 0;
	/** 
	 * The difference between [`maxY`]{@link module:box~Box#maxY} and [`minY`]{@link module:box~Box#minY}.
	 * @type {Number} 
	 */
	#height = 0;

	/** 
	 * The minimum (left-most) value of the x coordinates.
	 * @type {Number} 
	 * @example <caption>Move a box horizontally</caption>
	 * const box = new Box( 0, 0, 10, 10 );
	 * console.log( box.minX, box.maxX ); // Logs `0` `10`
	 * box.minX += 5;
	 * console.log( box.minX, box.maxX ); // Logs `5` `15`
	 */
	get minX() { return this.#minX; }
	set minX( value ) {
		if ( isNaN( value ) ) { return; }
		this.#minX = value;
		this.#maxX = value + this.#width;
	}
	/** 
	 * An alias for [`minX`]{@link module:box~Box#minX}. 
	 * @type {Number} 
	 * @example <caption>Move a box horizontally</caption>
	 * const box = new Box( 0, 0, 10, 10 );
	 * console.log( box.minX, box.maxX ); // Logs `0` `10`
	 * box.x += 50; // This is the same as `box.minX += 50`
	 * console.log( box.minX, box.maxX ); // Logs `50` `60`
	 */
	get x() { return this.#minX; }
	set x( value ) { this.minX = value; }

	/** 
	 * The maximum (right-most) value of the x coordinates. 
	 * @type {Number} 
	 * @example <caption>Move a box horizontally</caption>
	 * const box = new Box( 10, 10, 20, 20 );
	 * console.log( box.minX, box.maxX ); // Logs `10` `30`
	 * box.maxX += 15;
	 * console.log( box.minX, box.maxX ); // Logs `25` `45`
	 */
	get maxX() { return this.#maxX; }
	set maxX( value ) {
		if ( isNaN( value ) ) { return; }
		this.#maxX = value;
		this.#minX = value - this.#width;
	}

	/** 
	 * The minimum (top) value of the y coordinates. 
	 * @type {Number} 
	 * @example <caption>Move a box vertically</caption>
	 * const box = new Box( 0, 0, 10, 10 );
	 * console.log( box.minY, box.maxY ); // Logs `0` `10`
	 * box.minY += 5;
	 * console.log( box.minY, box.maxY ); // Logs `5` `15`
	 */
	get minY() { return this.#minY; }
	set minY( value ) {
		if ( isNaN( value ) ) { return; }
		this.#minY = value;
		this.#maxY = value + this.#height;
	}
	/** 
	 * An alias for [`minY`]{@link module:box~Box#minY}. 
	 * @type {Number} 
	 * @example <caption>Move a box vertically</caption>
	 * const box = new Box( 0, 0, 10, 10 );
	 * console.log( box.minY, box.maxY ); // Logs `0` `10`
	 * box.y += 50; // This is the same as `box.minY += 50`
	 * console.log( box.minY, box.maxY ); // Logs `50` `60` 
	 */
	get y() { return this.#minY; }
	set y( value ) { this.minY = value; }

	/** 
	 * The maximum (bottom) value of the y coordinates. 
	 * @type {Number} 
	 * @example <caption>Move a box vertically</caption>
	 * const box = new Box( 10, 10, 20, 20 );
	 * console.log( box.minY, box.maxY ); // Logs `10` `30`
	 * box.maxY += 15;
	 * console.log( box.minY, box.maxY ); // Logs `25` `45`
	 */
	get maxY() { return this.#maxY; }
	set maxY( value ) {
		if ( isNaN( value ) ) { return; }
		this.#maxY = value;
		this.#minY = value - this.#height;
	}

	/** 
	 * The difference between [`maxX`]{@link module:box~Box#maxX} and [`minX`]{@link module:box~Box#minX}. 
	 * Changing `width` alters [`maxX`]{@link module:box~Box#maxX}. 
	 * Cannot be a negative value.
	 * @type {Number} 
	 * @example <caption>Widen a box</caption>
	 * const box = new Box( 0, 0, 10, 10 );
	 * console.log( box.minX, box.maxX ); // Logs `0` `10`
	 * box.width += 20;
	 * console.log( box.minX, box.maxX ); // Logs `0` `30`
	 */
	get width() { return this.#width; }
	set width( value ) {
		if ( isNaN( value ) || ( value < 0 ) ) { return; }
		this.#width = value;
		this.#maxX = this.#minX + value;
	}

	/**
	 * The difference between [`maxY`]{@link module:box~Box#maxY} and [`minY`]{@link module:box~Box#minY}. 
	 * Changing `height` alters [`maxY`]{@link module:box~Box#maxY}. 
	 * Cannot be a negative value.
	 * @type {Number} 
	 * @example <caption>Heighten a box</caption>
	 * const box = new Box( 0, 0, 20, 20 );
	 * box.maxY; // is `20`
	 * box.height += 30;
	 * box.maxY; // is `50`
	 */
	get height() { return this.#height; }
	set height( value ) {
		if ( isNaN( value ) || ( value < 0 )  ) { return; }
		this.#height = value;
		this.#maxY = this.#minY + value;
	}

	/**
	 * [`width`]{@link module:box~Box#width} × [`height`]{@link module:box~Box#height}.
	 * @readonly
	 * @type {Number} 
	 * @example <caption>Get area of a box</caption>
	 * const box = new Box( 2, 4, Math.E, Math.PI ); // Create a e×π box at (2,4)
	 * box.area;                                     // is `8.539734222673566`
	 */
	get area() { return this.#width * this.#height; }

	/** 
	 * [Minimum value of `x`]{@link module:box~Box#minX} and [minimum value of `y`]{@link module:box~Box#minY}.
	 * @readonly
	 * @type {module:box~Coordinates} 
	 */
	get minima() { return { x: this.#minX, y: this.#minY }; }
	/** 
	 * [Maximum value of `x`]{@link module:box~Box#maxX} and [maximum value of `y`]{@link module:box~Box#maxY}.
	 * @readonly
	 * @type {module:box~Coordinates} 
	 */
	get maxima() { return { x: this.#maxX, y: this.#maxY }; }
	/** 
	 * [minima]{@link module:box~Box#minima} and [maxima]{@link module:box~Box#maxima} of the coordinates.
	 * @readonly
	 * @type {module:box~Extrema} 
	 */
	get extrema() { return { min: this.minima, max: this.maxima } };
	
	/** 
	 * An alias for [`minima`]{@link module:box~Box#minima}.
	 * @readonly
	 * @type {module:box~Coordinates} 
	 */
	get position() { return this.minima; }
	/** 
	 * [`width`]{@link module:box~Box#width} and [`height`]{@link module:box~Box#height}.
	 * @readonly
	 * @type {module:box~Size} 
	 */
	get size() { return { width: this.#width, height: this.#height } };
	/** 
	 * Position ([`x`]{@link module:box~Box#x} and [`y`]{@link module:box~Box#y}) and size ([`width`]{@link module:box~Box#width} and [`height`]{@link module:box~Box#height}).
	 * @readonly
	 * @type {module:box~Bounding} 
	 * @example <caption>Get bounding of a box</caption>
	 * const box = new Box( 0, 0, 10, 10 ); // Create a 10×10 box at (0,0)
	 * box.x += 10;                         // Move it horizontally by `10`
	 * box.y += 20;                         // Move it vertically by `20`
	 * box.width += 30;                     // Widen it by `30` 
	 * box.height += 40;                    // Heighten it by `40`
	 * box.bounding;                        // is `{ x: 10, y: 20, width: 40, height: 50 }`
	 */
	get bounding() { return {
		x: this.#minX, y: this.#minY, width: this.#width, height: this.#height
	} };

	/** 
	 * Ordered values that can be used with [`CanvasRenderingContext2D`]{@link https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/}'s [`rect()`]{@link https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/rect} or [`fillRect()`]{@link https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/rect} methods: [`minX`]{@link module:box~Box#minX}, [`minY`]{@link module:box~Box#minY}, [`width`]{@link module:box~Box#width}, and [`height`]{@link module:box~Box#height}.
	 * @readonly
	 * @type {Number[]} 
	 * @example <caption>Paint a box in a `canvas` element</caption>
	 * const box = new Box( 10, 20, 150, 100 );
	 * // Assuming `context` is a valid `CanvasRenderingContext2D` object:
	 * context.fillRect( ...box.rect );
	 * // Same as context.fillRect( box.x, box.y, box.width, box.height );
	 */
	get rect() { return [ this.#minX, this.#minY, this.#width, this.#height ]; }

	/** 
	 * All the basic properties of the box.
	 * @readonly
	 * @type {module:box~Properties} 
	 */
	get properties() { return {
		minX: this.#minX, minY: this.#minY, 
		maxX: this.#maxX, maxY: this.#maxY, 
		width: this.#width, height: this.#height
	} };

	/**
	 * Determine if `evaluated` is strictly above `comparedTo`.
	 * @param  {module:box~Box}     evaluated 
	 * @param  {module:box~Box}     comparedTo 
	 * @return {Boolean}
	 */
	static isAbove( evaluated, comparedTo ) {
		return evaluated.maxY < comparedTo.minY;
	}
	/**
	 * Determine if `evaluated` is strictly to the right of `comparedTo`.
	 * @param  {module:box~Box}     evaluated 
	 * @param  {module:box~Box}     comparedTo 
	 * @return {Boolean}
	 */
	static isRight( evaluated, comparedTo ) {
		return evaluated.minX > comparedTo.maxX;
	}
	/**
	 * Determine if `evaluated` is strictly below `comparedTo`.
	 * @param  {module:box~Box}     evaluated 
	 * @param  {module:box~Box}     comparedTo 
	 * @return {Boolean}
	 */
	static isBelow( evaluated, comparedTo ) {
		return evaluated.minY > comparedTo.maxY;
	}
	/**
	 * Determine if `evaluated` is strictly to the left of `comparedTo`.
	 * @param  {module:box~Box}     evaluated 
	 * @param  {module:box~Box}     comparedTo 
	 * @return {Boolean}
	 */
	static isLeft( evaluated, comparedTo ) {
		return evaluated.maxX < comparedTo.minX;
	}
	/**
	 * Determine if two boxes overlap, partially or totally.
	 * @param  {module:box~Box}     box1 
	 * @param  {module:box~Box}     box2
	 * @return {Boolean}
	 * @example <caption>Colliding boxes</caption>
	 * const box1 = new Box(  0,  0, 20, 20 );
	 * const box2 = new Box( 10, 10, 20, 20 );
	 * Box.areColliding( box1, box2 ); // is `true`
	 * @see {@link module:box~Box#isColliding|box.isColliding() dynamic method}
	 */
	static areColliding( box1, box2 ) {
		/*/
		const isAbove = Box.isAbove( box1, box2 );
		const isRight = Box.isRight( box1, box2 );
		const isBelow = Box.isBelow( box1, box2 );
		const isLeft = Box.isLeft( box1, box2 );
		return !( isAbove || isRight || isBelow || isLeft );
		/*/
		return (  
				 ( box1.minX <= box2.maxX )
			&& ( box1.maxX >= box2.minX )
			&& ( box1.minY <= box2.maxY )
			&& ( box1.maxY >= box2.minY )
		);
		/**/
	}
	/**
	 * Calculate the box resulting from an intersection of two boxes.
	 * @param  {module:box~Box} box1 
	 * @param  {module:box~Box} box2
	 * @return {(module:box~Box)?}
	 * @example <caption>Colliding boxes</caption>
	 * const box1 = new Box(  0,  0, 20, 20 );
	 * const box2 = new Box( 10, 10, 20, 20 );
	 * const box3 = Box.intersect( box1, box2 );
	 * box3.bounding; // is `{ x: 10, y: 10, width: 10, height: 10 }`
	 * @see {@link module:box~Box#intersect|box.intersect() dynamic method}
	 */
	static intersect( box1, box2 ) {
		if ( !Box.areColliding( box1, box2 ) ) { return null; }
		const minX = Math.max( box1.minX, box2.minX );
		const minY = Math.max( box1.minY, box2.minY );
		const maxX = Math.min( box1.maxX, box2.maxX );
		const maxY = Math.min( box1.maxY, box2.maxY );
		return Box.createFrom( {
			x: minX, 
			y: minY,
			width: ( maxX - minX ),
			height: ( maxY - minY )
		} );
	}

	/**
	 * Instances a `Box` from the properties of a [`Bounding`]{@link module:box~Bounding} object.
	 * @param  {module:box~Bounding} bounding 
	 *         An object with `x`, `y`, `width` and `height` properties.
	 * @return {(module:box~Box|Object)?} 
	 *         Returns the `bounding` input value if it doest no conform to the
	 *         [`Bounding`]{@link module:box~Bounding} specifiaction.
	 * @example <caption>Instance a box for a given bounding</caption>
	 * // These produce the same result:
	 * Box.createFrom( { x: 10, y: 20, width: 150, height: 100 } ); 
	 * new Box( 10, 20, 150, 100 );
	 */
	static createFrom( bounding ) {
		if ( !(
			   ( bounding instanceof Object )
			&& bounding.hasOwnProperty( "x" ) && !isNaN( bounding.x )
			&& bounding.hasOwnProperty( "y" ) && !isNaN( bounding.y )
			&& bounding.hasOwnProperty( "width" ) && !isNaN( bounding.width )
			&& bounding.hasOwnProperty( "height" ) && !isNaN( bounding.height )
			&& ( bounding.width >= 0 ) 
			&& ( bounding.height >= 0 ) 
		) ) { 
			return bounding; 
		}
		return ( 
			new Box ( bounding.x, bounding.y, bounding.width, bounding.height ) 
		);
	}

	/**
	 * Determine if the box is strictly above `anotherBox`.
	 * An alias for `[Box.isAbove]{@link module:box~Box.isAbove}( this, anotherBox )`.
	 * @param  {module:box~Box} anotherBox 
	 * @return {Boolean}
	 */
	isAbove( anotherBox ) { return Box.isAbove( this, anotherBox ); }
	/**
	 * Determine if the box is strictly to the right of `anotherBox`.
	 * An alias for `[Box.isRight]{@link module:box~Box.isRight}( this, anotherBox )`.
	 * @param  {module:box~Box}     anotherBox 
	 * @return {Boolean}
	 */
	isRight( anotherBox ) { return Box.isRight( this, anotherBox ); }
	/**
	 * Determine if the box is strictly below `anotherBox`.
	 * An alias for `[Box.isBelow]{@link module:box~Box.isBelow}( this, anotherBox )`.
	 * @param  {module:box~Box} anotherBox 
	 * @return {Boolean}
	 */
	isBelow( anotherBox ) { return Box.isBelow( this, anotherBox ); }
	/**
	 * Determine if the box is strictly to the left of `anotherBox`.
	 * An alias for `[Box.isLeft]{@link module:box~Box.isLeft}( this, anotherBox )`.
	 * @param  {module:box~Box} anotherBox 
	 * @return {Boolean}
	 */
	isLeft( anotherBox ) { return Box.isLeft( this, anotherBox ); }
	/**
	 * Determine if the box is overlapping `anotherBox`, partially or totally.
	 * An alias for `[Box.areColliding]{@link module:box~Box.areColliding}( this, anotherBox )`.
	 * @param  {module:box~Box} anotherBox 
	 * @return {Boolean}
	 * @example <caption>Colliding boxes</caption>
	 * const box1 = new Box(  0,  0, 20, 20 );
	 * const box2 = new Box( 10, 10, 20, 20 );
	 * box1.isColliding( box2 ); // is `true` 
	 * @see {@link module:box~Box.areColliding|Box.areColliding() static method}
	 */
	isColliding( anotherBox ) { return Box.areColliding( this, anotherBox ); }
	/**
	 * Calculate the box resulting from the intersection of the box with another box.
	 * An alias for `[Box.intersect]{@link module:box~Box.intersect}( this, anotherBox )`.
	 * @param  {module:box~Box} anotherBox 
	 * @return {(module:box~Box)?}
	 * @example <caption>Colliding boxes</caption>
	 * const box1 = new Box(  0,  0, 20, 20 );
	 * const box2 = new Box( 10, 10, 20, 20 );
	 * const box3 = box1.intersect( box2 );
	 * box3.bounding; // is `{ x: 10, y: 10, width: 10, height: 10 }`
	 * @see {@link module:box~Box.intersect|Box.intersect() static method}
	 */
	intersect( anotherBox ) { return Box.intersect( this, anotherBox ); }
	/**
	 * Determine if the box is completely inside `anotherBox`.
	 * @param   {module:box~Box} anotherBox
	 * @return {Boolean}
	 * @example <caption>Box in a box</caption>
	 * const box1 = new Box(  0,  0, 30, 30 );
	 * const box2 = new Box( 10, 10, 10, 10 );
	 * box2.isInside( box1 ); // is `true` 
	 * box1.isInside( box2 ); // is `false` 
	 */
	isInside( anotherBox ) { 
		return (  
			   ( this.minX >= anotherBox.minX )
			&& ( this.maxX <= anotherBox.maxX )
			&& ( this.minY >= anotherBox.minY )
			&& ( this.maxY <= anotherBox.maxY )
		);
	}

	/**
	 * Create a Box.
	 * Origin of coordinates is at the top left, with x increasing to the right
	 * and y increasing downwards.
	 * @param {Number} [x=0]      
	 * @param {Number} [y=0]
	 * @param {Number} [width=0]  Cannot be a negative number.
	 * @param {Number} [height=0] Cannot be a negative number.
	 * @example <caption>Colliding boxes</caption>
	 * const box1 = new Box(  0,  0, 20, 20 );
	 * const box2 = new Box( 10, 10, 20, 20 );
	 * box1.isColliding( box2 );       // is `true` 
	 * Box.areColliding( box1, box2 ); // is `true`
	 * const box3 = box1.intersect( box2 );
	 * box3.bounding; // is `{ x: 10, y: 10, width: 10, height: 10 }`
	 */
	constructor( x = 0, y = 0, width = 0, height = 0 ) {
		this.minX = x;
		this.minY = y;
		this.width = width;
		this.height = height;
	}

}

export { Box }